import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Shield, FileDown, AlertTriangle, CheckCircle2, ChevronRight,
  ChevronLeft, Plus, Trash2, Loader2, Info, FilePlus2,
  FileText, Save, Search, Wand2, FolderArchive, Clock,
  KeyRound, XCircle, Send, Lock, FlaskConical
} from 'lucide-react'
import { zipSync, strToU8 } from 'fflate'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { useToast } from '../hooks/useToast'
import { useWorkspace } from '../hooks/useWorkspace'
import SearchableSelect from '../components/SearchableSelect'

// ============================================================
// TYPES
// ============================================================

interface RapportoType {
  codice: string
  descrizione: string
  campiSaldi?: string
}

interface RuoloType {
  codice: string
  descrizione: string
}

interface CommType {
  codice: string
  descrizione: string
  record: string
}

interface TestataForm {
  tipoInvio: string
  tipoComunicazione: string
  annoRiferimento: string
  meseRiferimento: string
  codiceFiscaleObbligato: string
  cognome: string
  nome: string
  sesso: string
  dataNascita: string
  comuneNascita: string
  provinciaNascita: string
  denominazione: string
  comuneSedeLegale: string
  provinciaSedeLegale: string
  cfSocietaIncorporata: string
  protocolloAnnullamento: string
  flagCessazione: string
  dataChiusuraGeneralizzata: string
}

interface AnagraficaForm {
  codiceFiscale: string
  codiceRuolo: string
  cognome: string
  nome: string
  sesso: string
  dataNascita: string
  comuneNascita: string
  provinciaNascita: string
  denominazione: string
  comuneSedeLegale: string
  provinciaSedeLegale: string
  flagAssenzaCF: string
  dataInizioPartecipazione: string
  dataFinePartecipazione: string
}

interface SaldiForm {
  annoRiferimento: string
  importo1: string
  importo2: string
  importo3: string
  importo4: string
  importo5: string
  giacenzaMedia: string
  altreInformazioni: string
  valutaUIF: string
  naturaValuta: string
}

interface RapportoForm {
  codiceUnivocoRapporto: string
  tipoRapporto: string
  descrizioneRapporto: string
  dataInizio: string
  dataFine: string
  cab: string
  anagrafiche: AnagraficaForm[]
  saldi: SaldiForm | null
  includeSaldi: boolean
}

interface GenerationResult {
  content: string
  filename: string
  stats: {
    recordTipo0: number
    recordTipo1: number
    recordTipo2: number
    recordTipo3: number
    recordTipo4: number
    recordTipo9: number
    totalRecords: number
  }
  warnings: string[]
}

// ============================================================
// CONSTANTS
// ============================================================

const STEPS = ['rapporti', 'testata', 'riepilogo'] as const
type Step = typeof STEPS[number]

const EMPTY_ANAGRAFICA: AnagraficaForm = {
  codiceFiscale: '', codiceRuolo: '0', cognome: '', nome: '',
  sesso: '', dataNascita: '', comuneNascita: '', provinciaNascita: '',
  denominazione: '', comuneSedeLegale: '', provinciaSedeLegale: '',
  flagAssenzaCF: '0', dataInizioPartecipazione: '', dataFinePartecipazione: '',
}

const EMPTY_SALDI: SaldiForm = {
  annoRiferimento: '', importo1: '', importo2: '', importo3: '',
  importo4: '', importo5: '', giacenzaMedia: '', altreInformazioni: '',
  valutaUIF: 'EUR', naturaValuta: '',
}

const EMPTY_RAPPORTO: RapportoForm = {
  codiceUnivocoRapporto: '', tipoRapporto: '01',
  descrizioneRapporto: '', dataInizio: '', dataFine: '',
  cab: '', anagrafiche: [{ ...EMPTY_ANAGRAFICA }],
  saldi: null, includeSaldi: false,
}

const NO_DETAIL_TYPES = ['8', '9', 'C']
const RAPPORTO_ONLY_TYPES = ['4', '5']
const CAMBIO_ID_TYPE = '7'
const NO_SALDI_TYPES = ['08', '11', '13', '16', '17', '18', '19', '20', '21', '22', '97', '99']

interface CambioIdForm {
  vecchioCodice: string
  nuovoCodice: string
}

// ============================================================
// ============================================================
// HELPERS
// ============================================================

/** Converti data da input HTML (YYYY-MM-DD) a formato AdE (GGMMAAAA) */
function toAdeDate(htmlDate: string): string {
  if (!htmlDate) return ''
  const [y, m, d] = htmlDate.split('-')
  return `${d}${m}${y}`
}

/** Converti data da formato AdE (GGMMAAAA) a input HTML (YYYY-MM-DD) */
function fromAdeDate(adeDate: string | undefined): string {
  if (!adeDate || adeDate.length !== 8) return ''
  const d = adeDate.slice(0, 2)
  const m = adeDate.slice(2, 4)
  const y = adeDate.slice(4, 8)
  return `${y}-${m}-${d}`
}

/** Genera Codice Univoco Rapporto in formato standard:
 *  {CF_OBBLIGATO}_{YYYYMM}_{YYYYMMDD}_{HHMMSS}_{SEQ_10digit}
 *  Es: 04753510231_202601_20260303_112410_0000000001
 */
let codiceUnivocoCounter = 0
function generateCodiceUnivoco(
  cfObbligato: string,
  annoRif: string,
  meseRif: string,
  rapportoIndex: number
): string {
  const cf = cfObbligato.replace(/\s/g, '').toUpperCase()
  const now = new Date()
  const pad2 = (n: number) => String(n).padStart(2, '0')
  const yyyymm = `${annoRif}${meseRif}`
  const dateStr = `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}`
  const timeStr = `${pad2(now.getHours())}${pad2(now.getMinutes())}${pad2(now.getSeconds())}`
  codiceUnivocoCounter++
  const seq = String(codiceUnivocoCounter * 1000 + rapportoIndex + 1).padStart(10, '0')
  return `${cf}_${yyyymm}_${dateStr}_${timeStr}_${seq}`
}

/** Verifica se un codice fiscale è di tipo numerico (P.IVA 11 cifre) o alfanumerico (CF persona 16 char) */
function isPIVA(cf: string): boolean {
  return /^\d{11}$/.test(cf.trim())
}



// ============================================================
// COMPONENT
// ============================================================

export default function SidGenerator() {
  const { language } = useLanguage()
  useWorkspace()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const isIt = language === 'it'

  // ── Stato certificato SID ──
  const { data: certStatus } = useQuery<{ configured: boolean; uploadedAt?: string }>({
    queryKey: ['sid-certificato'],
    queryFn: async () => {
      const r = await api.get('/sid/certificato')
      return r.data.data
    },
  })
  const [certDismissed, setCertDismissed] = useState(false)

  // Wizard state
  const [step, setStep] = useState<Step>('rapporti')
  const [result, setResult] = useState<GenerationResult | null>(null)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [validationWarnings, setValidationWarnings] = useState<string[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [personaType, setPersonaType] = useState<'pf' | 'pnf'>('pnf')
  const [expandedRapporto, setExpandedRapporto] = useState<number>(0)

  // Form state
  const [testata, setTestata] = useState<TestataForm>({
    tipoInvio: '1', tipoComunicazione: '1',
    annoRiferimento: new Date().getFullYear().toString(),
    meseRiferimento: (new Date().getMonth() + 1).toString().padStart(2, '0'),
    codiceFiscaleObbligato: '', cognome: '', nome: '', sesso: '',
    dataNascita: '', comuneNascita: '', provinciaNascita: '',
    denominazione: '', comuneSedeLegale: '', provinciaSedeLegale: '',
    cfSocietaIncorporata: '', protocolloAnnullamento: '',
    flagCessazione: '0', dataChiusuraGeneralizzata: '',
  })
  const [rapporti, setRapporti] = useState<RapportoForm[]>([{ ...EMPTY_RAPPORTO }])
  const [cambiId, setCambiId] = useState<CambioIdForm[]>([{ vecchioCodice: '', nuovoCodice: '' }])
  const [lookupLoading, setLookupLoading] = useState<string | null>(null) // 'testata' | 'anag-{rapIdx}-{anagIdx}'
  const [editLoading, setEditLoading] = useState(false)
  const [sidMode, setSidMode] = useState<'sandbox' | 'produzione'>('sandbox')

  // ── Caricamento comunicazione in edit mode ──
  useEffect(() => {
    const editId = searchParams.get('edit')
    if (!editId || editingId === editId) return
    setEditLoading(true)
    api.get(`/sid/comunicazioni/${encodeURIComponent(editId)}`)
      .then(r => {
        const data = r.data?.data
        if (!data?.comunicazione) return
        const com = data.comunicazione
        setEditingId(editId)
        // Hydrate testata
        const t = com.testata || {}
        setTestata({
          tipoInvio: t.tipoInvio || '1',
          tipoComunicazione: t.tipoComunicazione || '1',
          annoRiferimento: t.annoRiferimento || '',
          meseRiferimento: t.meseRiferimento || '',
          codiceFiscaleObbligato: t.codiceFiscaleObbligato || '',
          cognome: t.cognome || '',
          nome: t.nome || '',
          sesso: t.sesso || '',
          dataNascita: fromAdeDate(t.dataNascita),
          comuneNascita: t.comuneNascita || '',
          provinciaNascita: t.provinciaNascita || '',
          denominazione: t.denominazione || '',
          comuneSedeLegale: t.comuneSedeLegale || '',
          provinciaSedeLegale: t.provinciaSedeLegale || '',
          cfSocietaIncorporata: t.cfSocietaIncorporata || '',
          protocolloAnnullamento: t.protocolloAnnullamento || '',
          flagCessazione: t.flagCessazione || '0',
          dataChiusuraGeneralizzata: fromAdeDate(t.dataChiusuraGeneralizzata),
        })
        // Detect persona type
        if (t.denominazione) setPersonaType('pnf')
        else if (t.cognome || t.nome) setPersonaType('pf')
        // Hydrate rapporti
        if (com.gruppiRapporto?.length) {
          setRapporti(com.gruppiRapporto.map((g: Record<string, unknown>) => {
            const rap = g.rapporto as Record<string, unknown> || {}
            const anags = (g.anagrafiche as Record<string, unknown>[]) || []
            const saldi = g.saldi as Record<string, unknown> | undefined
            return {
              codiceUnivocoRapporto: (rap.codiceUnivocoRapporto as string) || '',
              tipoRapporto: (rap.tipoRapporto as string) || '01',
              descrizioneRapporto: (rap.descrizioneRapporto as string) || '',
              dataInizio: fromAdeDate(rap.dataInizio as string),
              dataFine: fromAdeDate(rap.dataFine as string),
              cab: (rap.cab as string) || '',
              anagrafiche: anags.length > 0 ? anags.map((a) => ({
                codiceFiscale: (a.codiceFiscale as string) || '',
                codiceRuolo: (a.codiceRuolo as string) || '0',
                cognome: (a.cognome as string) || '',
                nome: (a.nome as string) || '',
                sesso: (a.sesso as string) || '',
                dataNascita: fromAdeDate(a.dataNascita as string),
                comuneNascita: (a.comuneNascita as string) || '',
                provinciaNascita: (a.provinciaNascita as string) || '',
                denominazione: (a.denominazione as string) || '',
                comuneSedeLegale: (a.comuneSedeLegale as string) || '',
                provinciaSedeLegale: (a.provinciaSedeLegale as string) || '',
                flagAssenzaCF: (a.flagAssenzaCF as string) || '0',
                dataInizioPartecipazione: fromAdeDate(a.dataInizioPartecipazione as string),
                dataFinePartecipazione: fromAdeDate(a.dataFinePartecipazione as string),
              })) : [{ ...EMPTY_ANAGRAFICA }],
              saldi: saldi ? {
                annoRiferimento: (saldi.annoRiferimento as string) || '',
                importo1: String(saldi.importo1 ?? ''),
                importo2: String(saldi.importo2 ?? ''),
                importo3: String(saldi.importo3 ?? ''),
                importo4: String(saldi.importo4 ?? ''),
                importo5: String(saldi.importo5 ?? ''),
                giacenzaMedia: String(saldi.giacenzaMedia ?? ''),
                altreInformazioni: String(saldi.altreInformazioni ?? ''),
                valutaUIF: (saldi.valutaUIF as string) || 'EUR',
                naturaValuta: (saldi.naturaValuta as string) || '',
              } : null,
              includeSaldi: !!saldi,
            }
          }))
        }
        // Hydrate cambi ID
        if (com.cambiId?.length) {
          setCambiId(com.cambiId.map((c: Record<string, string>) => ({
            vecchioCodice: c.vecchioCodice || '',
            nuovoCodice: c.nuovoCodice || '',
          })))
        }
        // Rimuovi ?edit dall'URL per evitare re-fetch
        setSearchParams({}, { replace: true })
      })
      .catch(err => console.error('Failed to load communication:', err))
      .finally(() => setEditLoading(false))
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  // Company lookup helper — chiama /business/company-lookup
  async function lookupCompanyByCF(cf: string): Promise<{
    denominazione: string
    comune: string
    provincia: string
  } | null> {
    if (!cf || cf.trim().length < 11) return null
    try {
      const searchType = isPIVA(cf) ? 'piva' : 'cf'
      const r = await api.post('/business/company-lookup', {
        query: cf.trim().toUpperCase(),
        searchType,
      })
      const results = r.data?.results
      if (results && results.length > 0) {
        const company = results[0]
        return {
          denominazione: company.denominazione || '',
          comune: company.sedeLegale?.comune || '',
          provincia: company.sedeLegale?.provincia || '',
        }
      }
      return null
    } catch {
      return null
    }
  }

  // Auto-detect PF/PNF from CF length: 16 = PF, 11 = PNF (P.IVA)
  function autoDetectPersonaType(cf: string) {
    const trimmed = cf.trim()
    if (trimmed.length === 16 && !/^\d{11}$/.test(trimmed)) {
      setPersonaType('pf')
    } else if (trimmed.length === 11 && /^\d{11}$/.test(trimmed)) {
      setPersonaType('pnf')
    }
  }

  // Auto-lookup per testata
  async function handleTestataCFLookup() {
    const cf = testata.codiceFiscaleObbligato.trim()
    if (!cf || cf.length < 11) return
    autoDetectPersonaType(cf)
    setLookupLoading('testata')
    try {
      const data = await lookupCompanyByCF(cf)
      if (data) {
        if (isPIVA(cf)) {
          setPersonaType('pnf')
          setTestata(p => ({
            ...p,
            denominazione: data.denominazione.toUpperCase(),
            comuneSedeLegale: data.comune.toUpperCase(),
            provinciaSedeLegale: data.provincia.toUpperCase(),
          }))
        } else {
          setPersonaType('pf')
          setTestata(p => ({
            ...p,
            denominazione: data.denominazione.toUpperCase(),
            comuneSedeLegale: data.comune.toUpperCase(),
            provinciaSedeLegale: data.provincia.toUpperCase(),
          }))
        }
      }
    } finally {
      setLookupLoading(null)
    }
  }

  // Auto-lookup per anagrafica nei rapporti
  async function handleAnagraficaCFLookup(rapIdx: number, anagIdx: number) {
    const anag = rapporti[rapIdx]?.anagrafiche[anagIdx]
    if (!anag) return
    const cf = anag.codiceFiscale.trim()
    if (!cf || cf.length < 11) return
    const loadingKey = `anag-${rapIdx}-${anagIdx}`
    setLookupLoading(loadingKey)
    try {
      const data = await lookupCompanyByCF(cf)
      if (data) {
        setRapporti(p => p.map((r, i) => i === rapIdx ? {
          ...r,
          anagrafiche: r.anagrafiche.map((a, k) => k === anagIdx ? {
            ...a,
            denominazione: data.denominazione.toUpperCase(),
            comuneSedeLegale: data.comune.toUpperCase(),
            provinciaSedeLegale: data.provincia.toUpperCase(),
          } : a)
        } : r))
      }
    } finally {
      setLookupLoading(null)
    }
  }

  // Genera codici univoci per tutti i rapporti
  function generateAllCodiciUnivoci() {
    setRapporti(prev => prev.map((rap, idx) => ({
      ...rap,
      codiceUnivocoRapporto: generateCodiceUnivoco(
        testata.codiceFiscaleObbligato,
        testata.annoRiferimento,
        testata.meseRiferimento,
        idx
      ),
    })))
  }

  // Reference data
  const { data: rapportoTypes } = useQuery<RapportoType[]>({
    queryKey: ['sid-rapporto-types'],
    queryFn: async () => {
      const r = await api.get('/sid/rapporto-types')
      return r.data.data
    },
    staleTime: Infinity,
  })
  const { data: ruoloTypes } = useQuery<RuoloType[]>({
    queryKey: ['sid-ruolo-types'],
    queryFn: async () => {
      const r = await api.get('/sid/ruolo-types')
      return r.data.data
    },
    staleTime: Infinity,
  })
  const { data: commTypesData } = useQuery<{ tipiComunicazione: CommType[], tipiInvio: { codice: string, descrizione: string }[] }>({
    queryKey: ['sid-comm-types'],
    queryFn: async () => {
      const r = await api.get('/sid/comm-types')
      return r.data.data
    },
    staleTime: Infinity,
  })

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (params: { generateFile: boolean }) => {
      const body = {
        comunicazione: buildRequestBody(),
        generateFile: params.generateFile,
        id: editingId || undefined,
      }
      const r = await api.post('/sid/comunicazioni', body)
      return r.data
    },
    onSuccess: (data) => {
      if (data.success) {
        setEditingId(data.data.id)
        queryClient.invalidateQueries({ queryKey: ['sid-comunicazioni'] })
        showToast(
          'success',
          isIt ? 'Bozza SID salvata correttamente.' : 'SID draft saved successfully.',
        )
      }
    },
    onError: (error: Error) => {
      showToast('error', error.message || (isIt ? 'Salvataggio non riuscito.' : 'Save failed.'))
    },
  })

  function resetForm() {
    setEditingId(null)
    setTestata({
      tipoInvio: '1', tipoComunicazione: '1',
      annoRiferimento: new Date().getFullYear().toString(),
      meseRiferimento: (new Date().getMonth() + 1).toString().padStart(2, '0'),
      codiceFiscaleObbligato: '', cognome: '', nome: '', sesso: '',
      dataNascita: '', comuneNascita: '', provinciaNascita: '',
      denominazione: '', comuneSedeLegale: '', provinciaSedeLegale: '',
      cfSocietaIncorporata: '', protocolloAnnullamento: '',
      flagCessazione: '0', dataChiusuraGeneralizzata: '',
    })
    setRapporti([{ ...EMPTY_RAPPORTO }])
    setCambiId([{ vecchioCodice: '', nuovoCodice: '' }])
    setResult(null)
    setValidationErrors([])
    setValidationWarnings([])
    setStep('rapporti')
  }

  /** Precompila il form con dati fittizi per test */
  function loadDemoData() {
    resetForm()
    setPersonaType('pnf')
    setTestata({
      tipoInvio: '1',
      tipoComunicazione: '1',
      annoRiferimento: '2025',
      meseRiferimento: '12',
      codiceFiscaleObbligato: '04753510231',
      cognome: '', nome: '', sesso: '',
      dataNascita: '', comuneNascita: '', provinciaNascita: '',
      denominazione: 'PONGO SRL',
      comuneSedeLegale: 'VERONA',
      provinciaSedeLegale: 'VR',
      cfSocietaIncorporata: '',
      protocolloAnnullamento: '',
      flagCessazione: '0',
      dataChiusuraGeneralizzata: '',
    })
    setRapporti([
      {
        codiceUnivocoRapporto: 'DEMO_PONGO_202512_001',
        tipoRapporto: '01',
        descrizioneRapporto: '',
        dataInizio: '2020-06-15',
        dataFine: '',
        cab: '59880',
        anagrafiche: [{
          codiceFiscale: 'PRNGPT89M09L781P',
          codiceRuolo: '0',
          cognome: 'PREGNOLATO',
          nome: 'GIAMPIETRO',
          sesso: 'M',
          dataNascita: '1989-08-09',
          comuneNascita: 'VERONA',
          provinciaNascita: 'VR',
          denominazione: '',
          comuneSedeLegale: '',
          provinciaSedeLegale: '',
          flagAssenzaCF: '0',
          dataInizioPartecipazione: '2020-06-15',
          dataFinePartecipazione: '',
        }],
        saldi: {
          annoRiferimento: '2025',
          importo1: '15000.50',
          importo2: '18200.00',
          importo3: '0',
          importo4: '0',
          importo5: '0',
          giacenzaMedia: '12500.75',
          altreInformazioni: '0',
          valutaUIF: 'EUR',
          naturaValuta: '',
        },
        includeSaldi: true,
      },
      {
        codiceUnivocoRapporto: 'DEMO_PONGO_202512_002',
        tipoRapporto: '03',
        descrizioneRapporto: '',
        dataInizio: '2022-01-10',
        dataFine: '',
        cab: '59880',
        anagrafiche: [{
          codiceFiscale: 'PRNGPT89M09L781P',
          codiceRuolo: '0',
          cognome: 'PREGNOLATO',
          nome: 'GIAMPIETRO',
          sesso: 'M',
          dataNascita: '1989-08-09',
          comuneNascita: 'VERONA',
          provinciaNascita: 'VR',
          denominazione: '',
          comuneSedeLegale: '',
          provinciaSedeLegale: '',
          flagAssenzaCF: '0',
          dataInizioPartecipazione: '2022-01-10',
          dataFinePartecipazione: '',
        }],
        saldi: {
          annoRiferimento: '2025',
          importo1: '50000.00',
          importo2: '48000.00',
          importo3: '0',
          importo4: '0',
          importo5: '0',
          giacenzaMedia: '45000.00',
          altreInformazioni: '0',
          valutaUIF: 'EUR',
          naturaValuta: '',
        },
        includeSaldi: true,
      },
    ])
  }

  // ── STEP VALIDATION ──────────────────────────────────────
  function validateTestataStep(): string[] {
    const errors: string[] = []
    const cf = testata.codiceFiscaleObbligato.trim()
    if (!cf) {
      errors.push(isIt ? 'Codice Fiscale / P.IVA del soggetto obbligato è obbligatorio.' : 'Obligated subject Tax Code / VAT is required.')
    } else if (cf.length < 11) {
      errors.push(isIt ? 'Codice Fiscale deve essere di almeno 11 caratteri (11 per P.IVA, 16 per CF).' : 'Tax code must be at least 11 characters.')
    }
    if (!testata.annoRiferimento || parseInt(testata.annoRiferimento) < 2000) {
      errors.push(isIt ? 'Anno di riferimento non valido.' : 'Reference year is invalid.')
    }
    const hasPF = testata.cognome.trim() || testata.nome.trim()
    const hasPNF = testata.denominazione.trim()
    if (!hasPF && !hasPNF) {
      errors.push(isIt ? 'Compilare i dati Persona Fisica (cognome/nome) oppure Persona Non Fisica (denominazione).' : 'Fill in individual data (surname/name) or legal entity data (company name).')
    }
    if (hasPF && !testata.sesso) {
      errors.push(isIt ? 'Sesso obbligatorio per persona fisica.' : 'Gender is required for individuals.')
    }
    if (hasPF && !testata.dataNascita) {
      errors.push(isIt ? 'Data di nascita obbligatoria per persona fisica.' : 'Birth date is required for individuals.')
    }
    if (testata.tipoComunicazione === '9' && !testata.protocolloAnnullamento.trim()) {
      errors.push(isIt ? 'Protocollo da annullare obbligatorio per comunicazione tipo 9 (Annullamento).' : 'Cancellation protocol is required for type 9.')
    }
    if (testata.tipoComunicazione === 'C' && !testata.dataChiusuraGeneralizzata) {
      errors.push(isIt ? 'Data chiusura generalizzata obbligatoria per comunicazione tipo C.' : 'Generalized closure date is required for type C.')
    }
    return errors
  }

  function validateRapportiStep(): string[] {
    const errors: string[] = []
    if (needsCambiId) {
      if (cambiId.length === 0) {
        errors.push(isIt ? 'Aggiungere almeno una coppia cambio identificativo.' : 'Add at least one ID change pair.')
      }
      cambiId.forEach((c, idx) => {
        if (!c.vecchioCodice.trim()) {
          errors.push(isIt ? `Coppia ${idx + 1}: vecchio codice obbligatorio.` : `Pair ${idx + 1}: old code required.`)
        }
        if (!c.nuovoCodice.trim()) {
          errors.push(isIt ? `Coppia ${idx + 1}: nuovo codice obbligatorio.` : `Pair ${idx + 1}: new code required.`)
        }
      })
    } else if (needsRapporti) {
      if (rapporti.length === 0) {
        errors.push(isIt ? 'Aggiungere almeno un rapporto.' : 'Add at least one account.')
      }
      rapporti.forEach((rap, idx) => {
        if (!rap.codiceUnivocoRapporto.trim()) {
          errors.push(isIt ? `Rapporto ${idx + 1}: codice univoco obbligatorio.` : `Account ${idx + 1}: unique code required.`)
        }
        if (!rap.dataInizio) {
          errors.push(isIt ? `Rapporto ${idx + 1}: data inizio obbligatoria.` : `Account ${idx + 1}: start date required.`)
        }
        const isRapportoOnly = RAPPORTO_ONLY_TYPES.includes(testata.tipoComunicazione)
        if (!isRapportoOnly) {
          rap.anagrafiche.forEach((anag, j) => {
            if (!anag.codiceFiscale.trim()) {
              errors.push(isIt ? `Rapporto ${idx + 1}, Anagrafica ${j + 1}: codice fiscale obbligatorio.` : `Account ${idx + 1}, Participant ${j + 1}: tax code required.`)
            }
            if (!anag.dataInizioPartecipazione) {
              errors.push(isIt ? `Rapporto ${idx + 1}, Anagrafica ${j + 1}: data inizio partecipazione obbligatoria.` : `Account ${idx + 1}, Participant ${j + 1}: participation start date required.`)
            }
          })
        }
      })
    }
    // No-detail types don't need validation here
    return errors
  }

  function handleNext() {
    let errors: string[] = []
    if (step === 'rapporti') {
      errors = validateRapportiStep()
      if (errors.length === 0) {
        setValidationErrors([])
        setStep('testata')
      }
    } else if (step === 'testata') {
      errors = validateTestataStep()
      if (errors.length === 0) {
        setValidationErrors([])
        // Auto-genera codici univoci per rapporti che non li hanno ancora
        if (testata.codiceFiscaleObbligato.trim()) {
          setRapporti(prev => prev.map((rap, idx) => {
            if (!rap.codiceUnivocoRapporto.trim()) {
              return { ...rap, codiceUnivocoRapporto: generateCodiceUnivoco(
                testata.codiceFiscaleObbligato, testata.annoRiferimento, testata.meseRiferimento, idx
              )}
            }
            return rap
          }))
        }
        setStep('riepilogo')
      }
    }
    if (errors.length > 0) {
      setValidationErrors(errors)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  function handleBack() {
    setValidationErrors([])
    setValidationWarnings([])
    if (step === 'testata') setStep('rapporti')
    else if (step === 'riepilogo') setStep('testata')
  }

  // Whether current comm type needs rapporti section
  const needsRapporti = !NO_DETAIL_TYPES.includes(testata.tipoComunicazione) && testata.tipoComunicazione !== CAMBIO_ID_TYPE
  const needsCambiId = testata.tipoComunicazione === CAMBIO_ID_TYPE

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      // Generate + save in one call
      const body = {
        comunicazione: buildRequestBody(),
        generateFile: true,
        id: editingId || undefined,
      }
      const r = await api.post('/sid/comunicazioni', body)
      return r.data
    },
    onSuccess: (data) => {
      if (data.success) {
        setEditingId(data.data.id)
        setValidationErrors([])
        setValidationWarnings(data.data.warnings || [])
        queryClient.invalidateQueries({ queryKey: ['sid-comunicazioni'] })
        // We need to also get the generated content for preview
        fetchGeneratedContent()
        showToast(
          'success',
          isIt ? 'File SID generato e salvato nello storico.' : 'SID file generated and stored in history.',
        )
      } else {
        setValidationErrors(data.errors || ['Errore sconosciuto'])
        setValidationWarnings(data.warnings || [])
        setResult(null)
      }
    },
    onError: (err: Error) => {
      setValidationErrors([err.message || 'Errore di rete'])
      setValidationWarnings([])
      setResult(null)
      showToast('error', err.message || (isIt ? 'Generazione non riuscita.' : 'Generation failed.'))
    },
  })

  async function fetchGeneratedContent() {
    try {
      // Also generate locally for preview (the saved version is in R2)
      const body = buildRequestBody()
      const r = await api.post('/sid/generate', body)
      if (r.data.success) {
        setResult(r.data.data)
      }
    } catch {
      // Preview not critical - file is saved in R2
    }
  }

  // Validate mutation
  const validateMutation = useMutation({
    mutationFn: async () => {
      const body = buildRequestBody()
      const r = await api.post('/sid/validate', body)
      return r.data
    },
    onSuccess: (data) => {
      setValidationErrors(data.data?.errors || [])
      setValidationWarnings(data.data?.warnings || [])
    },
  })

  const signMutation = useMutation({
    mutationFn: async () => {
      if (!editingId) throw new Error('Salva prima la comunicazione')
      const r = await api.post(`/sid/comunicazioni/${editingId}/sign`, { mode: sidMode })
      if (!r.data.success) throw new Error(r.data.error || 'Firma fallita')
      return r.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sid-comunicazioni'] })
      const pecStatus = data?.data?.pec_send?.status
      if (pecStatus === 'success') {
        showToast(
          'success',
          isIt
            ? 'Firma completata e invio PEC avviato con tracking automatico.'
            : 'Signature completed and PEC delivery tracking started.',
        )
        return
      }
      showToast(
        'success',
        isIt ? 'Firma SID completata.' : 'SID signature completed.',
      )
    },
    onError: (error: Error) => {
      showToast('error', error.message || (isIt ? 'Firma non riuscita.' : 'Signature failed.'))
    },
  })

  function buildRequestBody() {
    const gruppiRapporto = rapporti.map((rap) => ({
      rapporto: {
        codiceUnivocoRapporto: rap.codiceUnivocoRapporto,
        tipoRapporto: rap.tipoRapporto,
        descrizioneRapporto: rap.descrizioneRapporto,
        dataInizio: toAdeDate(rap.dataInizio),
        dataFine: rap.dataFine ? toAdeDate(rap.dataFine) : undefined,
        cab: rap.cab,
      },
      anagrafiche: rap.anagrafiche.map((anag, j) => ({
        codiceUnivocoRapporto: rap.codiceUnivocoRapporto,
        progressivo: j + 1,
        codiceFiscale: anag.codiceFiscale,
        codiceRuolo: anag.codiceRuolo,
        cognome: anag.cognome,
        nome: anag.nome,
        sesso: anag.sesso,
        dataNascita: toAdeDate(anag.dataNascita),
        comuneNascita: anag.comuneNascita,
        provinciaNascita: anag.provinciaNascita,
        denominazione: anag.denominazione,
        comuneSedeLegale: anag.comuneSedeLegale,
        provinciaSedeLegale: anag.provinciaSedeLegale,
        flagAssenzaCF: anag.flagAssenzaCF,
        dataInizioPartecipazione: toAdeDate(anag.dataInizioPartecipazione),
        dataFinePartecipazione: anag.dataFinePartecipazione ? toAdeDate(anag.dataFinePartecipazione) : undefined,
      })),
      saldi: rap.includeSaldi && rap.saldi ? {
        codiceUnivocoRapporto: rap.codiceUnivocoRapporto,
        tipoRapporto: rap.tipoRapporto,
        annoRiferimento: rap.saldi.annoRiferimento || testata.annoRiferimento,
        importo1: parseFloat(rap.saldi.importo1) || 0,
        importo2: parseFloat(rap.saldi.importo2) || 0,
        importo3: parseFloat(rap.saldi.importo3) || 0,
        importo4: parseFloat(rap.saldi.importo4) || 0,
        importo5: parseFloat(rap.saldi.importo5) || 0,
        giacenzaMedia: parseFloat(rap.saldi.giacenzaMedia) || 0,
        altreInformazioni: parseFloat(rap.saldi.altreInformazioni) || 0,
        valutaUIF: rap.saldi.valutaUIF || 'EUR',
        naturaValuta: rap.saldi.naturaValuta,
      } : undefined,
    }))

    return {
      testata: {
        tipoInvio: testata.tipoInvio,
        tipoComunicazione: testata.tipoComunicazione,
        annoRiferimento: testata.annoRiferimento,
        meseRiferimento: testata.meseRiferimento,
        codiceFiscaleObbligato: testata.codiceFiscaleObbligato.toUpperCase(),
        cognome: testata.cognome.toUpperCase(),
        nome: testata.nome.toUpperCase(),
        sesso: testata.sesso,
        dataNascita: toAdeDate(testata.dataNascita),
        comuneNascita: testata.comuneNascita.toUpperCase(),
        provinciaNascita: testata.provinciaNascita.toUpperCase(),
        denominazione: testata.denominazione.toUpperCase(),
        comuneSedeLegale: testata.comuneSedeLegale.toUpperCase(),
        provinciaSedeLegale: testata.provinciaSedeLegale.toUpperCase(),
        cfSocietaIncorporata: testata.cfSocietaIncorporata.toUpperCase(),
        protocolloAnnullamento: testata.protocolloAnnullamento,
        flagCessazione: testata.flagCessazione,
        dataChiusuraGeneralizzata: testata.dataChiusuraGeneralizzata ? toAdeDate(testata.dataChiusuraGeneralizzata) : undefined,
      },
      gruppiRapporto: needsRapporti ? gruppiRapporto : undefined,
      cambiId: needsCambiId ? cambiId.filter(c => c.vecchioCodice || c.nuovoCodice).map(c => ({
        vecchioCodice: c.vecchioCodice,
        nuovoCodice: c.nuovoCodice,
      })) : undefined,
    }
  }

  /** Trigger programmatic file download — cross-browser safe (Safari needs DOM attachment) */
  function triggerDownload(blob: Blob, fname: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fname
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    // Delay revoke so the browser has time to start the download
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, 250)
  }

  /** Download ZIP containing the TXT file with proper naming for SID Desktop */
  async function downloadZip(savedId?: string, filename?: string) {
    try {
      // Get TXT content from same cascade as downloadFile
      let txtContent: string | null = null
      let txtFilename = filename || 'comunicazione_sid.txt'

      // 1. Try R2
      if (savedId || editingId) {
        try {
          const r = await api.get(`/sid/comunicazioni/${savedId || editingId}/download`, { responseType: 'text' })
          txtContent = r.data as string
          txtFilename = filename || result?.filename || 'comunicazione_sid.txt'
        } catch { /* fallback */ }
      }

      // 2. Fallback: local result
      if (!txtContent && result?.content) {
        txtContent = result.content
        txtFilename = result.filename || txtFilename
      }

      // 3. Last resort: re-generate
      if (!txtContent) {
        const body = buildRequestBody()
        const r = await api.post('/sid/generate', body)
        if (r.data.success && r.data.data?.content) {
          txtContent = r.data.data.content
          txtFilename = r.data.data.filename || txtFilename
          setResult(r.data.data)
        }
      }

      if (!txtContent) {
        alert(isIt ? 'Impossibile ottenere il contenuto del file.' : 'Unable to get file content.')
        return
      }

      // Try to also get the signed .p7m file
      const zipFiles: Record<string, Uint8Array> = { [txtFilename]: strToU8(txtContent) }
      const targetId = savedId || editingId
      if (targetId) {
        try {
          const p7mResp = await api.get(`/sid/comunicazioni/${targetId}/download-p7m`, { responseType: 'arraybuffer' })
          if (p7mResp.status === 200 && p7mResp.data) {
            const p7mFilename = txtFilename.replace(/\.txt$/i, '.p7m')
            zipFiles[p7mFilename] = new Uint8Array(p7mResp.data as ArrayBuffer)
          }
        } catch { /* .p7m not available, skip */ }
      }

      // Build ZIP with fflate
      const zipData = zipSync(zipFiles)
      const zipFilename = txtFilename.replace(/\.txt$/i, '.zip')
      const blob = new Blob([zipData.buffer as ArrayBuffer], { type: 'application/zip' })
      triggerDownload(blob, zipFilename)
    } catch (e) {
      console.error('ZIP download error:', e)
      alert(isIt ? 'Errore durante la creazione dello ZIP.' : 'Error creating ZIP file.')
    }
  }

  /** Apre il report PDF (HTML) in una nuova finestra per stampa/PDF */
  async function openPdfReport(savedId?: string) {
    const id = savedId || editingId
    if (!id) {
      alert(isIt ? 'Salva prima la comunicazione per generare il PDF.' : 'Save the communication first to generate the PDF.')
      return
    }
    try {
      const r = await api.get(`/sid/comunicazioni/${id}/pdf`, { responseType: 'text' })
      const html = r.data as string
      const w = window.open('', 'sid-pdf-print', 'width=1000,height=700')
      if (w) {
        w.document.write(html)
        w.document.close()
      }
    } catch (e) {
      console.error('PDF report error:', e)
      alert(isIt ? 'Errore nella generazione del report PDF.' : 'Error generating PDF report.')
    }
  }

  async function downloadFile(savedId?: string, filename?: string) {
    // 1. Try downloading from R2 via dedicated endpoint
    if (savedId) {
      try {
        const r = await api.get(`/sid/comunicazioni/${savedId}/download`, { responseType: 'blob' })
        const blob = new Blob([r.data], { type: 'text/plain;charset=utf-8' })
        triggerDownload(blob, filename || 'comunicazione_sid.txt')
        return
      } catch (e: unknown) {
        console.error('Download from R2 failed:', e)
        // Fall through to fallback if we have local content
      }
    }

    // 2. If we have an editingId but no savedId passed, try R2
    if (!savedId && editingId) {
      return downloadFile(editingId, result?.filename || filename)
    }

    // 3. Fallback: use local generation result if available
    if (result?.content && result?.filename) {
      const blob = new Blob([result.content], { type: 'text/plain;charset=utf-8' })
      triggerDownload(blob, result.filename)
      return
    }

    // 4. Last resort: re-generate locally and download
    try {
      const body = buildRequestBody()
      const r = await api.post('/sid/generate', body)
      if (r.data.success && r.data.data?.content) {
        const blob = new Blob([r.data.data.content], { type: 'text/plain;charset=utf-8' })
        triggerDownload(blob, r.data.data.filename || 'comunicazione_sid.txt')
        setResult(r.data.data)
        return
      }
    } catch {
      // re-generate also failed
    }

    // Nothing worked
    alert(isIt
      ? 'Impossibile scaricare il file. Prova a rigenerarlo dalla scheda Riepilogo.'
      : 'Unable to download the file. Try regenerating it from the Summary tab.')
  }

  // ============================================================
  // STEP: TESTATA
  // ============================================================

  function renderTestata() {
    const tipiComm = commTypesData?.tipiComunicazione || []
    const tipiInvio = commTypesData?.tipiInvio || []

    return (
      <div className="space-y-6">
        {/* Validation errors */}
        {validationErrors.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 animate-in fade-in slide-in-from-top-2">
            <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" /> {isIt ? 'Campi da compilare' : 'Fields to complete'}
            </h4>
            <ul className="text-xs text-red-600 dark:text-red-400 space-y-1 list-disc list-inside">
              {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {isIt
              ? 'Compila i dati del soggetto obbligato e il tipo di comunicazione. Tutti i campi obbligatori sono contrassegnati con *.'
              : 'Fill in the obligated subject data and communication type. All mandatory fields are marked with *.'}
          </p>
        </div>

        {/* Tipo invio e comunicazione */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isIt ? 'Tipo Invio *' : 'Submission Type *'}
            </label>
            <SearchableSelect
              value={testata.tipoInvio}
              onChange={v => setTestata(p => ({ ...p, tipoInvio: v }))}
              options={[
                ...(tipiInvio.map(t => ({ value: t.codice, label: `${t.codice} - ${t.descrizione}` }))),
                ...(tipiInvio.length === 0 ? [
                  { value: '1', label: '1 - Ordinario' },
                  { value: '2', label: '2 - Sostitutivo' },
                  { value: '3', label: '3 - Straordinario' },
                ] : []),
              ]}
              placeholder={isIt ? 'Seleziona tipo invio...' : 'Select submission type...'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isIt ? 'Tipo Comunicazione *' : 'Communication Type *'}
            </label>
            <SearchableSelect
              value={testata.tipoComunicazione}
              onChange={v => setTestata(p => ({ ...p, tipoComunicazione: v }))}
              options={[
                ...(tipiComm.map(t => ({ value: t.codice, label: `${t.codice} - ${t.descrizione}` }))),
                ...(tipiComm.length === 0 ? [
                  { value: '1', label: '1 - Nuovi rapporti' },
                  { value: '2', label: '2 - Aggiornamento' },
                  { value: '3', label: '3 - Saldi annuali' },
                  { value: '4', label: '4 - Cancellazione' },
                  { value: '5', label: '5 - Chiusura' },
                  { value: '6', label: '6 - Reimpianti' },
                  { value: '7', label: '7 - Cambio ID' },
                  { value: '8', label: '8 - Presa in carico' },
                  { value: '9', label: '9 - Annullamento' },
                  { value: 'C', label: 'C - Chiusura generalizzata' },
                ] : []),
              ]}
              placeholder={isIt ? 'Seleziona tipo comunicazione...' : 'Select communication type...'}
            />
          </div>
        </div>

        {/* Anno e Mese */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isIt ? 'Anno Riferimento *' : 'Reference Year *'}
            </label>
            <input
              type="number" min="2000" max="2100"
              value={testata.annoRiferimento}
              onChange={e => setTestata(p => ({ ...p, annoRiferimento: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isIt ? 'Mese Riferimento' : 'Reference Month'}
            </label>
            <select
              value={testata.meseRiferimento}
              onChange={e => setTestata(p => ({ ...p, meseRiferimento: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
            >
              <option value="00">-- {isIt ? 'Non applicabile' : 'N/A'}</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                  {String(i + 1).padStart(2, '0')}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* CF Obbligato */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {isIt ? 'Codice Fiscale Soggetto Obbligato *' : 'Obligated Subject Tax Code *'}
          </label>
          <div className="flex gap-2">
            <input
              type="text" maxLength={16}
              value={testata.codiceFiscaleObbligato}
              onChange={e => setTestata(p => ({ ...p, codiceFiscaleObbligato: e.target.value.toUpperCase() }))}
              onBlur={() => {
                const cf = testata.codiceFiscaleObbligato.trim()
                if (cf.length >= 11 && !testata.denominazione) {
                  handleTestataCFLookup()
                }
              }}
              placeholder={isIt ? 'CF persona fisica o P.IVA' : 'Tax code or VAT number'}
              className="flex-1 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono"
            />
            <button
              onClick={handleTestataCFLookup}
              disabled={lookupLoading === 'testata' || testata.codiceFiscaleObbligato.trim().length < 11}
              title={isIt ? 'Cerca dati azienda da CF/P.IVA' : 'Look up company data from tax code'}
              className="px-3 py-2 rounded-lg border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:border-gray-200 dark:disabled:border-slate-600 disabled:text-gray-400 dark:disabled:text-slate-500 disabled:cursor-not-allowed text-sm flex items-center gap-1.5 transition-colors whitespace-nowrap"
            >
              {lookupLoading === 'testata' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {isIt ? 'Cerca' : 'Lookup'}
            </button>
          </div>
          {lookupLoading === 'testata' && (
            <p className="text-xs text-teal-600 dark:text-teal-400 mt-1 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> {isIt ? 'Ricerca dati azienda...' : 'Looking up company...'}
            </p>
          )}
        </div>

        {/* Tab PF / PNF */}
        <div className="border border-gray-200 dark:border-slate-600 rounded-lg overflow-hidden">
          <div className="flex border-b border-gray-200 dark:border-slate-600">
            <button
              type="button"
              onClick={() => setPersonaType('pf')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                personaType === 'pf'
                  ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-b-2 border-teal-600'
                  : 'bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-600'
              }`}
            >
              {isIt ? 'Persona Fisica (PF)' : 'Individual (PF)'}
            </button>
            <button
              type="button"
              onClick={() => setPersonaType('pnf')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                personaType === 'pnf'
                  ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-b-2 border-teal-600'
                  : 'bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-600'
              }`}
            >
              {isIt ? 'Persona Non Fisica (PNF)' : 'Legal Entity (PNF)'}
            </button>
          </div>

          <div className="p-4">
            {personaType === 'pf' ? (
              /* Dati PF: cognome, nome, sesso, data nascita, comune, prov */
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Cognome' : 'Surname'}</label>
                  <input type="text" maxLength={26} value={testata.cognome}
                    onChange={e => setTestata(p => ({ ...p, cognome: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Nome' : 'Name'}</label>
                  <input type="text" maxLength={25} value={testata.nome}
                    onChange={e => setTestata(p => ({ ...p, nome: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Sesso' : 'Gender'}</label>
                  <select value={testata.sesso}
                    onChange={e => setTestata(p => ({ ...p, sesso: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm">
                    <option value="">--</option>
                    <option value="M">M</option>
                    <option value="F">F</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Data Nascita' : 'Birth Date'}</label>
                  <input type="date" value={testata.dataNascita}
                    onChange={e => setTestata(p => ({ ...p, dataNascita: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Comune Nascita' : 'Birth City'}</label>
                  <input type="text" maxLength={40} value={testata.comuneNascita}
                    onChange={e => setTestata(p => ({ ...p, comuneNascita: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Provincia' : 'Province'}</label>
                  <input type="text" maxLength={2} value={testata.provinciaNascita}
                    onChange={e => setTestata(p => ({ ...p, provinciaNascita: e.target.value.toUpperCase() }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono" />
                </div>
              </div>
            ) : (
              /* Dati PNF: denominazione, comune/prov sede legale */
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Denominazione' : 'Company Name'}</label>
                  <input type="text" maxLength={70} value={testata.denominazione}
                    onChange={e => setTestata(p => ({ ...p, denominazione: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Comune Sede' : 'HQ City'}</label>
                  <input type="text" maxLength={40} value={testata.comuneSedeLegale}
                    onChange={e => setTestata(p => ({ ...p, comuneSedeLegale: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Provincia' : 'Province'}</label>
                  <input type="text" maxLength={2} value={testata.provinciaSedeLegale}
                    onChange={e => setTestata(p => ({ ...p, provinciaSedeLegale: e.target.value.toUpperCase() }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Campi condizionali */}
        {testata.tipoComunicazione === '9' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isIt ? 'Protocollo da annullare *' : 'Protocol to cancel *'}
            </label>
            <input type="text" maxLength={24} value={testata.protocolloAnnullamento}
              onChange={e => setTestata(p => ({ ...p, protocolloAnnullamento: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono" />
          </div>
        )}

        {['7', '8'].includes(testata.tipoComunicazione) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isIt ? 'CF Società Incorporata' : 'Incorporated Company Tax Code'}
            </label>
            <input type="text" maxLength={16} value={testata.cfSocietaIncorporata}
              onChange={e => setTestata(p => ({ ...p, cfSocietaIncorporata: e.target.value.toUpperCase() }))}
              className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono" />
          </div>
        )}

        {testata.tipoComunicazione === 'C' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {isIt ? 'Data Chiusura Generalizzata *' : 'Generalized Closure Date *'}
              </label>
              <input type="date" value={testata.dataChiusuraGeneralizzata}
                onChange={e => setTestata(p => ({ ...p, dataChiusuraGeneralizzata: e.target.value, flagCessazione: '1' }))}
                className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
            </div>
          </div>
        )}
      </div>
    )
  }

  // ============================================================
  // STEP: RAPPORTI
  // ============================================================

  function renderRapporti() {
    // Validation errors display
    const errorsBlock = validationErrors.length > 0 ? (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 mb-4">
        <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4" /> {isIt ? 'Campi da compilare' : 'Fields to complete'}
        </h4>
        <ul className="text-xs text-red-600 dark:text-red-400 space-y-1 list-disc list-inside">
          {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      </div>
    ) : null

    // Tipo 7 - Cambio Identificativo: mostra form coppie vecchio/nuovo codice
    if (needsCambiId) {
      return (
        <div className="space-y-4">
          {errorsBlock}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {isIt ? 'Coppie Cambio Identificativo' : 'ID Change Pairs'}
            </h3>
            <button onClick={() => setCambiId(p => [...p, { vecchioCodice: '', nuovoCodice: '' }])}
              className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors">
              <Plus className="w-3 h-3" /> {isIt ? 'Aggiungi coppia' : 'Add pair'}
            </button>
          </div>
          {cambiId.map((c, idx) => (
            <div key={idx} className="border border-gray-200 dark:border-slate-600 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {isIt ? 'Coppia' : 'Pair'} #{idx + 1}
                </span>
                {cambiId.length > 1 && (
                  <button onClick={() => setCambiId(p => p.filter((_, i) => i !== idx))}
                    className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {isIt ? 'Vecchio Codice Univoco *' : 'Old Unique Code *'}
                  </label>
                  <input type="text" maxLength={50} value={c.vecchioCodice}
                    onChange={e => setCambiId(p => p.map((item, i) => i === idx ? { ...item, vecchioCodice: e.target.value } : item))}
                    placeholder={isIt ? 'Codice identificativo precedente' : 'Previous identifier'}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {isIt ? 'Nuovo Codice Univoco *' : 'New Unique Code *'}
                  </label>
                  <input type="text" maxLength={50} value={c.nuovoCodice}
                    onChange={e => setCambiId(p => p.map((item, i) => i === idx ? { ...item, nuovoCodice: e.target.value } : item))}
                    placeholder={isIt ? 'Nuovo codice identificativo' : 'New identifier'}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (!needsRapporti) {
      return (
        <div>
          {errorsBlock}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
            <Info className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {isIt
                ? `La comunicazione di tipo ${testata.tipoComunicazione} non prevede record di dettaglio.`
                : `Communication type ${testata.tipoComunicazione} does not require detail records.`}
            </p>
          </div>
        </div>
      )
    }

    const isRapportoOnly = RAPPORTO_ONLY_TYPES.includes(testata.tipoComunicazione)

    return (
      <div className="space-y-6">
        {errorsBlock}
        {/* Pulsante genera tutti i codici univoci */}
        <div className="flex justify-end">
          <button
            onClick={generateAllCodiciUnivoci}
            disabled={!testata.codiceFiscaleObbligato.trim()}
            title={isIt ? 'Genera automaticamente tutti i codici univoci' : 'Auto-generate all unique codes'}
            className="flex items-center gap-2 text-sm border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:border-gray-200 dark:disabled:border-slate-600 disabled:text-gray-400 dark:disabled:text-slate-500 disabled:cursor-not-allowed px-4 py-2 rounded-lg transition-colors"
          >
            <Wand2 className="w-4 h-4" />
            {isIt ? 'Genera tutti i codici univoci' : 'Generate all unique codes'}
          </button>
        </div>

        {rapporti.map((rap, idx) => (
          <div key={idx} className="border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden">
            {/* Rapporto header — click to expand/collapse */}
            <button
              type="button"
              onClick={() => setExpandedRapporto(prev => prev === idx ? -1 : idx)}
              className="w-full bg-gray-50 dark:bg-slate-700/50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expandedRapporto === idx ? 'rotate-90' : ''}`} />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {isIt ? 'Rapporto' : 'Account'} #{idx + 1}
                  {rap.codiceUnivocoRapporto && (
                    <span className="ml-2 text-xs font-normal text-gray-400 font-mono">{rap.codiceUnivocoRapporto}</span>
                  )}
                </h3>
              </div>
              {rapporti.length > 1 && (
                <span
                  onClick={e => { e.stopPropagation(); setRapporti(p => p.filter((_, i) => i !== idx)) }}
                  className="text-red-500 hover:text-red-700 p-1">
                  <Trash2 className="w-4 h-4" />
                </span>
              )}
            </button>
            {expandedRapporto === idx && (
            <div className="p-4 space-y-4">
              {/* Rapporto fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {isIt ? 'Codice Univoco *' : 'Unique Code *'}
                  </label>
                  <div className="flex gap-1.5">
                    <input type="text" maxLength={50} value={rap.codiceUnivocoRapporto}
                      onChange={e => {
                        const v = e.target.value
                        setRapporti(p => p.map((r, i) => i === idx ? { ...r, codiceUnivocoRapporto: v } : r))
                      }}
                      className="flex-1 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono" />
                    <button
                      onClick={() => {
                        const code = generateCodiceUnivoco(
                          testata.codiceFiscaleObbligato,
                          testata.annoRiferimento,
                          testata.meseRiferimento,
                          idx
                        )
                        setRapporti(p => p.map((r, i) => i === idx ? { ...r, codiceUnivocoRapporto: code } : r))
                      }}
                      disabled={!testata.codiceFiscaleObbligato.trim()}
                      title={isIt ? 'Genera automaticamente' : 'Auto-generate'}
                      className="px-2 py-2 rounded-lg border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:border-gray-200 dark:disabled:border-slate-600 disabled:text-gray-400 dark:disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
                    >
                      <Wand2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {isIt ? 'Tipo Rapporto *' : 'Account Type *'}
                  </label>
                  <SearchableSelect
                    value={rap.tipoRapporto}
                    onChange={v => setRapporti(p => p.map((r, i) => i === idx ? { ...r, tipoRapporto: v } : r))}
                    options={(rapportoTypes || []).map(rt => ({ value: rt.codice, label: `${rt.codice} - ${rt.descrizione}` }))}
                    placeholder={isIt ? 'Cerca tipo rapporto...' : 'Search account type...'}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">CAB</label>
                  <input type="text" maxLength={5} value={rap.cab}
                    onChange={e => {
                      const v = e.target.value
                      setRapporti(p => p.map((r, i) => i === idx ? { ...r, cab: v } : r))
                    }}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {isIt ? 'Data Inizio *' : 'Start Date *'}
                  </label>
                  <input type="date" value={rap.dataInizio}
                    onChange={e => {
                      const v = e.target.value
                      setRapporti(p => p.map((r, i) => i === idx ? { ...r, dataInizio: v } : r))
                    }}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {isIt ? 'Data Fine' : 'End Date'}
                  </label>
                  <input type="date" value={rap.dataFine}
                    onChange={e => {
                      const v = e.target.value
                      setRapporti(p => p.map((r, i) => i === idx ? { ...r, dataFine: v } : r))
                    }}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
                </div>
              </div>

              {/* Anagrafiche (hidden for rapporto-only types) */}
              {!isRapportoOnly && (
                <fieldset className="border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 mt-4">
                  <legend className="text-sm font-medium text-indigo-600 dark:text-indigo-400 px-2">
                    {isIt ? 'Anagrafiche' : 'Participants'}
                  </legend>
                  {rap.anagrafiche.map((anag, j) => (
                    <div key={j} className={`${j > 0 ? 'mt-4 pt-4 border-t border-gray-100 dark:border-slate-600' : ''}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500">
                          {isIt ? 'Anagrafica' : 'Participant'} #{j + 1}
                        </span>
                        {rap.anagrafiche.length > 1 && (
                          <button onClick={() => {
                            setRapporti(p => p.map((r, i) => i === idx ? { ...r, anagrafiche: r.anagrafiche.filter((_, k) => k !== j) } : r))
                          }} className="text-red-400 hover:text-red-600 text-xs">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">CF *</label>
                          <div className="flex gap-1">
                            <input type="text" maxLength={16} value={anag.codiceFiscale}
                              onChange={e => {
                                const v = e.target.value.toUpperCase()
                                setRapporti(p => p.map((r, i) => i === idx ? {
                                  ...r, anagrafiche: r.anagrafiche.map((a, k) => k === j ? { ...a, codiceFiscale: v } : a)
                                } : r))
                              }}
                              onBlur={() => {
                                const cf = anag.codiceFiscale.trim()
                                if (cf.length >= 11 && !anag.denominazione) {
                                  handleAnagraficaCFLookup(idx, j)
                                }
                              }}
                              className="flex-1 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 text-xs font-mono" />
                            <button
                              onClick={() => handleAnagraficaCFLookup(idx, j)}
                              disabled={lookupLoading === `anag-${idx}-${j}` || anag.codiceFiscale.trim().length < 11}
                              title={isIt ? 'Cerca dati da CF/P.IVA' : 'Lookup from tax code'}
                              className="px-1.5 py-1.5 rounded-lg border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:border-gray-200 dark:disabled:border-slate-600 disabled:text-gray-400 dark:disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
                            >
                              {lookupLoading === `anag-${idx}-${j}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Ruolo *' : 'Role *'}</label>
                          <SearchableSelect
                            value={anag.codiceRuolo}
                            onChange={v => setRapporti(p => p.map((r, i) => i === idx ? {
                              ...r, anagrafiche: r.anagrafiche.map((a, k) => k === j ? { ...a, codiceRuolo: v } : a)
                            } : r))}
                            options={(ruoloTypes || []).map(rt => ({ value: rt.codice, label: `${rt.codice} - ${rt.descrizione}` }))}
                            placeholder={isIt ? 'Cerca ruolo...' : 'Search role...'}
                            size="sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Cognome' : 'Surname'}</label>
                          <input type="text" maxLength={26} value={anag.cognome}
                            onChange={e => {
                              const v = e.target.value
                              setRapporti(p => p.map((r, i) => i === idx ? {
                                ...r, anagrafiche: r.anagrafiche.map((a, k) => k === j ? { ...a, cognome: v } : a)
                              } : r))
                            }}
                            className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 text-xs" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Nome' : 'Name'}</label>
                          <input type="text" maxLength={25} value={anag.nome}
                            onChange={e => {
                              const v = e.target.value
                              setRapporti(p => p.map((r, i) => i === idx ? {
                                ...r, anagrafiche: r.anagrafiche.map((a, k) => k === j ? { ...a, nome: v } : a)
                              } : r))
                            }}
                            className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 text-xs" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Inizio Partecip. *' : 'Start Date *'}</label>
                          <input type="date" value={anag.dataInizioPartecipazione}
                            onChange={e => {
                              const v = e.target.value
                              setRapporti(p => p.map((r, i) => i === idx ? {
                                ...r, anagrafiche: r.anagrafiche.map((a, k) => k === j ? { ...a, dataInizioPartecipazione: v } : a)
                              } : r))
                            }}
                            className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 text-xs" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Fine Partecip.' : 'End Date'}</label>
                          <input type="date" value={anag.dataFinePartecipazione}
                            onChange={e => {
                              const v = e.target.value
                              setRapporti(p => p.map((r, i) => i === idx ? {
                                ...r, anagrafiche: r.anagrafiche.map((a, k) => k === j ? { ...a, dataFinePartecipazione: v } : a)
                              } : r))
                            }}
                            className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 text-xs" />
                        </div>
                      </div>
                      {/* Dati PNF: Denominazione, Sede, Provincia — popolati automaticamente dal lookup CF/P.IVA */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {isIt ? 'Denominazione (PNF)' : 'Company Name (entity)'}
                          </label>
                          <input type="text" maxLength={70} value={anag.denominazione}
                            onChange={e => {
                              const v = e.target.value
                              setRapporti(p => p.map((r, i) => i === idx ? {
                                ...r, anagrafiche: r.anagrafiche.map((a, k) => k === j ? { ...a, denominazione: v } : a)
                              } : r))
                            }}
                            className={`w-full rounded-lg border px-2 py-1.5 text-xs ${anag.denominazione ? 'border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'}`} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {isIt ? 'Sede Legale' : 'HQ City'}
                          </label>
                          <input type="text" maxLength={40} value={anag.comuneSedeLegale}
                            onChange={e => {
                              const v = e.target.value
                              setRapporti(p => p.map((r, i) => i === idx ? {
                                ...r, anagrafiche: r.anagrafiche.map((a, k) => k === j ? { ...a, comuneSedeLegale: v } : a)
                              } : r))
                            }}
                            className={`w-full rounded-lg border px-2 py-1.5 text-xs ${anag.comuneSedeLegale ? 'border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'}`} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {isIt ? 'Provincia' : 'Province'}
                          </label>
                          <input type="text" maxLength={2} value={anag.provinciaSedeLegale}
                            onChange={e => {
                              const v = e.target.value.toUpperCase()
                              setRapporti(p => p.map((r, i) => i === idx ? {
                                ...r, anagrafiche: r.anagrafiche.map((a, k) => k === j ? { ...a, provinciaSedeLegale: v } : a)
                              } : r))
                            }}
                            className={`w-full rounded-lg border px-2 py-1.5 text-xs font-mono ${anag.provinciaSedeLegale ? 'border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'}`} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => setRapporti(p => p.map((r, i) => i === idx ? {
                      ...r, anagrafiche: [...r.anagrafiche, { ...EMPTY_ANAGRAFICA }]
                    } : r))}
                    className="mt-3 flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800">
                    <Plus className="w-3 h-3" /> {isIt ? 'Aggiungi anagrafica' : 'Add participant'}
                  </button>
                </fieldset>
              )}

              {/* Saldi (hidden for rapporto-only types and no-saldi rapporto types) */}
              {!isRapportoOnly && !NO_SALDI_TYPES.includes(rap.tipoRapporto) && (
                <fieldset className="border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mt-4">
                  <legend className="text-sm font-medium text-emerald-600 dark:text-emerald-400 px-2 flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={rap.includeSaldi}
                        onChange={e => {
                          const v = e.target.checked
                          setRapporti(p => p.map((r, i) => i === idx ? {
                            ...r, includeSaldi: v, saldi: v ? (r.saldi || { ...EMPTY_SALDI }) : null
                          } : r))
                        }}
                        className="rounded" />
                      {isIt ? 'Saldi e Movimenti' : 'Balances & Movements'}
                    </label>
                  </legend>
                  {rap.includeSaldi && rap.saldi && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Anno Saldi *' : 'Year *'}</label>
                        <input type="number" min="2000" max="2100"
                          value={rap.saldi.annoRiferimento}
                          onChange={e => {
                            const v = e.target.value
                            setRapporti(p => p.map((r, i) => i === idx ? {
                              ...r, saldi: r.saldi ? { ...r.saldi, annoRiferimento: v } : null
                            } : r))
                          }}
                          className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 text-xs" />
                      </div>
                      {['importo1', 'importo2', 'importo3', 'importo4', 'importo5', 'giacenzaMedia', 'altreInformazioni'].map(field => (
                        <div key={field}>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {field === 'giacenzaMedia' ? (isIt ? 'Giacenza Media' : 'Average Balance')
                              : field === 'altreInformazioni' ? (isIt ? 'Altre Info' : 'Other Info')
                              : field.replace('importo', 'Imp. ')}
                          </label>
                          <input type="number" step="1"
                            value={rap.saldi ? rap.saldi[field as keyof SaldiForm] : ''}
                            onChange={e => {
                              const v = e.target.value
                              setRapporti(p => p.map((r, i) => i === idx ? {
                                ...r, saldi: r.saldi ? { ...r.saldi, [field]: v } : null
                              } : r))
                            }}
                            className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 text-xs font-mono" />
                        </div>
                      ))}
                    </div>
                  )}
                </fieldset>
              )}
            </div>
            )}
          </div>
        ))}

        <button
          onClick={() => {
            const newIdx = rapporti.length
            const newRapporto = { ...EMPTY_RAPPORTO }
            // Auto-genera il codice univoco per il nuovo rapporto
            if (testata.codiceFiscaleObbligato.trim()) {
              newRapporto.codiceUnivocoRapporto = generateCodiceUnivoco(
                testata.codiceFiscaleObbligato,
                testata.annoRiferimento,
                testata.meseRiferimento,
                newIdx
              )
            }
            setRapporti(p => [...p, newRapporto])
            setExpandedRapporto(newIdx)
          }}
          className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 font-medium">
          <FilePlus2 className="w-4 h-4" /> {isIt ? 'Aggiungi rapporto' : 'Add account'}
        </button>
      </div>
    )
  }

  // ============================================================
  // STEP: RIEPILOGO + GENERAZIONE
  // ============================================================

  function renderRiepilogo() {
    return (
      <div className="space-y-6">
        {/* Validation results */}
        {validationErrors.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" /> {isIt ? 'Errori di validazione' : 'Validation errors'}
            </h4>
            <ul className="text-xs text-red-600 dark:text-red-400 space-y-1 list-disc list-inside">
              {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {validationWarnings.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-yellow-700 dark:text-yellow-300 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" /> {isIt ? 'Avvertimenti' : 'Warnings'}
            </h4>
            <ul className="text-xs text-yellow-600 dark:text-yellow-400 space-y-1 list-disc list-inside">
              {validationWarnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {/* Summary */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
            {isIt ? 'Riepilogo Comunicazione' : 'Communication Summary'}
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <span className="text-gray-500">{isIt ? 'Tipo Invio' : 'Submission Type'}:</span>
            <span className="font-medium">{testata.tipoInvio}</span>
            <span className="text-gray-500">{isIt ? 'Tipo Comunicazione' : 'Communication Type'}:</span>
            <span className="font-medium">{testata.tipoComunicazione} - {commTypesData?.tipiComunicazione.find(t => t.codice === testata.tipoComunicazione)?.descrizione || ''}</span>
            <span className="text-gray-500">{isIt ? 'Anno/Mese' : 'Year/Month'}:</span>
            <span className="font-medium">{testata.annoRiferimento}/{testata.meseRiferimento}</span>
            <span className="text-gray-500">CF:</span>
            <span className="font-medium font-mono">{testata.codiceFiscaleObbligato}</span>
            {needsRapporti && <>
              <span className="text-gray-500">{isIt ? 'N° Rapporti' : 'Accounts'}:</span>
              <span className="font-medium">{rapporti.length}</span>
            </>}
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-green-700 dark:text-green-300 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> {isIt ? 'File generato con successo!' : 'File generated successfully!'}
              </h4>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadFile()}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700">
                  <FileDown className="w-3 h-3" /> TXT
                </button>
                <button onClick={() => downloadZip()}
                  className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700">
                  <FolderArchive className="w-3 h-3" /> ZIP
                </button>
                <button onClick={() => openPdfReport()}
                  className="flex items-center gap-2 px-3 py-1.5 bg-rose-600 text-white text-xs rounded-lg hover:bg-rose-700">
                  <FileText className="w-3 h-3" /> PDF
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
              <div className="bg-white/50 dark:bg-slate-800/50 rounded p-2 text-center">
                <div className="font-semibold text-sm">{result.stats.totalRecords}</div>
                <div className="text-gray-500">{isIt ? 'Record totali' : 'Total records'}</div>
              </div>
              <div className="bg-white/50 dark:bg-slate-800/50 rounded p-2 text-center">
                <div className="font-semibold text-sm">{result.stats.recordTipo1}</div>
                <div className="text-gray-500">{isIt ? 'Rapporti' : 'Accounts'}</div>
              </div>
              <div className="bg-white/50 dark:bg-slate-800/50 rounded p-2 text-center">
                <div className="font-semibold text-sm">{result.stats.recordTipo2}</div>
                <div className="text-gray-500">{isIt ? 'Anagrafiche' : 'Participants'}</div>
              </div>
              <div className="bg-white/50 dark:bg-slate-800/50 rounded p-2 text-center">
                <div className="font-semibold text-sm">{result.stats.recordTipo3}</div>
                <div className="text-gray-500">{isIt ? 'Saldi' : 'Balances'}</div>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {isIt ? 'File' : 'File'}: <span className="font-mono">{result.filename}</span>
            </div>
            {/* Link to Storico */}
            <Link
              to="/sid/storico"
              className="mt-3 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 font-medium transition-colors"
            >
              <Clock className="w-4 h-4" />
              {isIt ? 'Vai allo Storico per gestire i download' : 'Go to History to manage downloads'}
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
            {/* Preview */}
            <details className="mt-3">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                {isIt ? 'Anteprima file (prime 10 righe)' : 'File preview (first 10 lines)'}
              </summary>
              <pre className="mt-2 bg-black/80 text-green-400 text-[10px] font-mono p-3 rounded-lg overflow-x-auto max-h-48">
                {result.content.split('\n').slice(0, 10).join('\n')}
              </pre>
            </details>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => saveMutation.mutate({ generateFile: false })}
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50">
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isIt ? 'Salva Bozza' : 'Save Draft'}
          </button>
          <button
            onClick={() => validateMutation.mutate()}
            disabled={validateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 text-sm rounded-lg hover:bg-yellow-200 disabled:opacity-50">
            {validateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {isIt ? 'Valida' : 'Validate'}
          </button>
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            {isIt ? 'Genera e Salva TXT' : 'Generate & Save TXT'}
          </button>
        </div>
        {(saveMutation.isSuccess && !generateMutation.isPending) && (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <CheckCircle2 className="w-4 h-4" />
            {isIt ? 'Bozza salvata nel workspace' : 'Draft saved to workspace'}
          </div>
        )}

        {/* ── Firma e Invio ── */}
        {editingId && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 space-y-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {isIt ? 'Firma e Preparazione Invio' : 'Signing & Submission Preparation'}
            </h4>

            {/* Toggle Sandbox / Produzione */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">{isIt ? 'Modalità:' : 'Mode:'}</span>
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden">
                <button
                  onClick={() => setSidMode('sandbox')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${
                    sidMode === 'sandbox'
                      ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                      : 'bg-white dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-600'
                  }`}
                >
                  <FlaskConical className="w-3.5 h-3.5" />
                  Sandbox
                </button>
                <button
                  onClick={() => setSidMode('produzione')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${
                    sidMode === 'produzione'
                      ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                      : 'bg-white dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-600'
                  }`}
                >
                  <Lock className="w-3.5 h-3.5" />
                  {isIt ? 'Produzione' : 'Production'}
                </button>
              </div>
            </div>

            {/* Avviso modalità */}
            <div className={`flex items-start gap-2 text-xs rounded-lg p-3 ${
              sidMode === 'sandbox'
                ? 'bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
                : 'bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
            }`}>
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
              {sidMode === 'sandbox'
                ? (isIt
                    ? 'Sandbox: la firma viene eseguita con un certificato di test. Nessun dato viene inviato all\'Agenzia delle Entrate.'
                    : 'Sandbox: signing is performed with a test certificate. No data is sent to the Revenue Agency.')
                  : (isIt
                    ? 'Produzione: firma con certificato del workspace e invio PEC automatico attivi. Il canale diretto Entratel/DCSE non è abilitato.'
                    : 'Production: workspace certificate signing and automatic PEC sending are active. Direct Entratel/DCSE submission is not enabled.')}
              </span>
            </div>

            {/* Pulsante Firma */}
            <button
              onClick={() => signMutation.mutate()}
              disabled={signMutation.isPending}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg disabled:opacity-50 transition ${
                sidMode === 'sandbox'
                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {signMutation.isPending
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Send className="w-4 h-4" />}
              {sidMode === 'sandbox'
                ? (isIt ? 'Firma (Sandbox)' : 'Sign (Sandbox)')
                : (isIt ? 'Firma (Produzione)' : 'Sign (Production)')}
            </button>

            {sidMode === 'produzione' && (
              <p className="text-xs text-red-500 dark:text-red-400">
                {isIt
                  ? 'Invio diretto AdE non attivo: il flusso operativo resta PEC automatica + canale manuale Entratel/DCSE quando richiesto. '
                  : 'Direct Revenue Agency submission is not active: operational flow is automatic PEC + manual Entratel/DCSE channel when required. '}
                <Link to="/sid/certificato" className="underline font-medium">
                  {isIt ? 'Gestisci qui i certificati del workspace' : 'Manage workspace certificates here'}
                </Link>
              </p>
            )}

            {/* Risultato firma */}
            {signMutation.isSuccess && (
              <div className="flex items-start gap-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{isIt ? 'Firma completata con successo!' : 'Signing completed successfully!'}</p>
                  <p className="text-xs mt-1 text-green-600 dark:text-green-500">
                    {isIt ? 'Il file .p7m firmato è stato salvato.' : 'The signed .p7m file has been saved.'}
                  </p>
                </div>
              </div>
            )}
            {signMutation.isError && (
              <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{isIt ? 'Firma fallita' : 'Signing failed'}</p>
                  <p className="text-xs mt-1">{(signMutation.error as Error)?.message || 'Errore sconosciuto'}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // ============================================================
  // MAIN RENDER
  // ============================================================

  const stepIdx = STEPS.indexOf(step)

  return (
    <div className="space-y-6 pt-4">
      {/* ── Banner certificato mancante ── */}
      {!certDismissed && certStatus && !certStatus.configured && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 shadow-sm">
          <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-red-800 dark:text-red-200">
              {isIt ? 'Certificato SID non configurato' : 'SID Certificate not configured'}
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              {isIt
                ? 'Per firmare e attivare l\'invio PEC automatico in Produzione è necessario caricare il certificato .p12 rilasciato dal Desktop Telematico.'
                : 'To sign communications and enable automatic PEC sending in Production, you must upload the .p12 certificate issued by Desktop Telematico.'}
            </p>
            <Link
              to="/sid/certificato"
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
            >
              <KeyRound className="w-4 h-4" />
              {isIt ? 'Carica certificato' : 'Upload certificate'}
            </Link>
          </div>
          <button
            onClick={() => setCertDismissed(true)}
            className="text-red-400 hover:text-red-600 dark:hover:text-red-300"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Loading indicator for edit mode */}
      {editLoading && (
        <div className="flex items-center justify-center gap-3 p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {isIt ? 'Caricamento comunicazione...' : 'Loading communication...'}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          {/* Page title now in top bar */}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isIt
              ? 'Genera file TXT conformi alle specifiche AdE per l\'Archivio dei Rapporti Finanziari (SID)'
              : 'Generate AdE-compliant TXT files for the Financial Records Archive (SID)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadDemoData}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
          >
            <FileText className="w-4 h-4" />
            Demo
          </button>
          <button
            onClick={resetForm}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
          >
            <FilePlus2 className="w-4 h-4" />
            {isIt ? 'Nuova Comunicazione' : 'New Communication'}
          </button>
        </div>
      </div>

      {/* Editing indicator */}
      {editingId && (
        <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg text-sm text-indigo-700 dark:text-indigo-300">
          <FileText className="w-4 h-4" />
          {isIt ? 'Stai modificando una comunicazione salvata.' : 'Editing a saved communication.'}
          <button onClick={resetForm} className="ml-auto text-xs underline hover:text-indigo-900 dark:hover:text-indigo-100">
            {isIt ? 'Nuova comunicazione' : 'New communication'}
          </button>
        </div>
      )}

      {/* Steps */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            {i > 0 && <ChevronRight className="w-4 h-4 text-gray-300" />}
            <button
              onClick={() => setStep(s)}
              className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                step === s
                  ? 'bg-indigo-600 text-white'
                  : i < stepIdx
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                    : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-gray-400'
              }`}
            >
              {i + 1}. {s === 'testata' ? (isIt ? 'Testata' : 'Header')
                : s === 'rapporti' ? (isIt ? 'Rapporti' : 'Accounts')
                : (isIt ? 'Riepilogo' : 'Summary')}
            </button>
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-6">
        {step === 'testata' && renderTestata()}
        {step === 'rapporti' && renderRapporti()}
        {step === 'riepilogo' && renderRiepilogo()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handleBack}
          disabled={stepIdx === 0}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronLeft className="w-4 h-4" /> {isIt ? 'Indietro' : 'Back'}
        </button>
        <button
          onClick={handleNext}
          disabled={stepIdx === STEPS.length - 1}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-30 disabled:cursor-not-allowed">
          {isIt ? 'Avanti' : 'Next'} <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
